import { useMemo, useState } from 'react';
import { Icon, type IconName } from '../../components/Icon.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';

/**
 * Enterprise access, as the screens show it: departments, the access level of each permission
 * (see, work, approve, set up), and the duties a business keeps apart (packages/core/src/role-catalog.ts).
 */

export type Level = 'self' | 'view' | 'use' | 'approve' | 'admin';
export const LEVELS: readonly Level[] = ['view', 'use', 'approve', 'admin'];
const RANK: Record<Level, number> = { self: 2, view: 1, use: 2, approve: 3, admin: 4 };
export const levelRank = (l: Level) => RANK[l];

export const AREAS = ['general', 'admin', 'finance', 'sales', 'marketing', 'service', 'supply', 'operations', 'services', 'people', 'hospitality', 'public', 'workplace'] as const;
export type Area = (typeof AREAS)[number];
export const areaLabel = (a: string) => t(`area.${a}` as MessageKey);
const AREA_ICONS: Record<string, IconName> = {
  general: 'star',
  admin: 'settings',
  finance: 'bank',
  sales: 'trendUp',
  marketing: 'send',
  service: 'lifebuoy',
  supply: 'box',
  operations: 'factory',
  services: 'wrench',
  people: 'users',
  hospitality: 'chef',
  public: 'book',
  workplace: 'layout',
  custom: 'sliders',
};
export const areaIcon = (a: string): IconName => AREA_ICONS[a] ?? 'shield';
export const levelLabel = (l: Level | 'none') => t(`access.level_${l === 'self' ? 'use' : l}` as MessageKey);

export interface Perm {
  name: string;
  label: string;
  level?: Level;
}
export interface DutyDef {
  key: string;
  a: string[];
  b: string[];
}
export type DutyPolicy = 'off' | 'warn' | 'block';

/** The duties these permissions combine that a business keeps apart. */
export function dutyClashes(perms: ReadonlySet<string>, defs: readonly DutyDef[]): string[] {
  return defs.filter((d) => d.a.some((p) => perms.has(p)) && d.b.some((p) => perms.has(p))).map((d) => d.key);
}
export const dutyText = (key: string) => t(`duty.${key}` as MessageKey);

/** A warning (or, under "stop", a refusal) that one person would hold duties kept apart. */
export function DutyNotice({ clashes, policy, subject }: { clashes: string[]; policy: DutyPolicy; subject: 'role' | 'person' }) {
  if (!clashes.length || policy === 'off') return null;
  const stop = policy === 'block';
  return (
    <div className={`callout ${stop ? 'callout-danger' : 'callout-warn'} duty-notice`} role={stop ? 'alert' : 'status'}>
      <Icon name={stop ? 'lock' : 'alert'} size={18} />
      <div>
        <p>{stop ? t(subject === 'role' ? 'duty.blocked_role' : 'duty.blocked_person') : t(subject === 'role' ? 'duty.warn_role' : 'duty.warn_person')}</p>
        <ul>
          {clashes.map((k) => (
            <li key={k}>{dutyText(k)}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export interface PickRole {
  name: string;
  label: string;
  hint: string;
  area: string;
  permissions?: Perm[];
  ownOnly?: boolean;
  conflicts?: string[];
}

/**
 * Choosing roles for a person: by department, with a search, what each role is for, and a
 * warning when the roles together hold duties the business keeps apart.
 */
export function RolePicker({
  roles,
  value,
  onChange,
  disabled,
  duties,
  policy = 'warn',
  idPrefix = 'pick',
}: {
  roles: PickRole[];
  value: string[];
  onChange: (v: string[]) => void;
  disabled?: boolean;
  duties?: readonly DutyDef[];
  policy?: DutyPolicy;
  idPrefix?: string;
}) {
  const [query, setQuery] = useState('');
  const [area, setArea] = useState<string>('all');
  const q = query.trim().toLowerCase();
  const areas = useMemo(() => {
    const present = new Set(roles.map((r) => r.area));
    return [...(present.has('custom') ? ['custom'] : []), ...AREAS.filter((a) => present.has(a))];
  }, [roles]);
  const shown = roles.filter((r) => (area === 'all' || r.area === area) && (!q || r.label.toLowerCase().includes(q) || r.hint.toLowerCase().includes(q)));
  const chosen = roles.filter((r) => value.includes(r.name));
  const clashes = useMemo(() => {
    if (!duties?.length || value.includes('owner')) return [];
    return dutyClashes(new Set(chosen.flatMap((r) => (r.permissions ?? []).map((p) => p.name))), duties);
  }, [chosen, duties, value]);
  const toggle = (name: string) => onChange(value.includes(name) ? value.filter((x) => x !== name) : [...value, name]);
  return (
    <div className="role-picker">
      {chosen.length ? (
        <div className="role-picker-chosen" aria-live="polite">
          <span className="muted small">{tn('access.chosen_roles', chosen.length)}</span>
          <span className="chips">
            {chosen.map((r) => (
              <button key={r.name} type="button" className="chip active" disabled={disabled} onClick={() => toggle(r.name)} aria-label={t('access.remove_role', { name: r.label })}>
                {r.label} <Icon name="x" size={14} />
              </button>
            ))}
          </span>
        </div>
      ) : null}
      {duties ? <DutyNotice clashes={clashes} policy={policy} subject="person" /> : null}
      <div className="role-picker-tools">
        <div className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" placeholder={t('access.find_role')} aria-label={t('access.find_role')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <label className="role-picker-area">
          <span className="sr-only">{t('access.department')}</span>
          <select className="input" value={area} onChange={(e) => setArea(e.target.value)} aria-label={t('access.department')}>
            <option value="all">{t('access.all_departments')}</option>
            {areas.map((a) => (
              <option key={a} value={a}>
                {a === 'custom' ? t('roles.yours') : areaLabel(a)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="role-picker-list">
        {areas
          .map((a) => ({ a, list: shown.filter((r) => r.area === a) }))
          .filter((x) => x.list.length)
          .map(({ a, list }) => (
            <section key={a} className="role-picker-group" aria-label={a === 'custom' ? t('roles.yours') : areaLabel(a)}>
              <h4>
                <Icon name={areaIcon(a)} size={14} /> {a === 'custom' ? t('roles.yours') : areaLabel(a)}
              </h4>
              {list.map((r) => {
                const on = value.includes(r.name);
                const id = `${idPrefix}-${r.name}`;
                return (
                  <div key={r.name} className={`role-option${on ? ' on' : ''}`}>
                    <input id={id} type="checkbox" checked={on} disabled={disabled} onChange={() => toggle(r.name)} />
                    <span>
                      <label htmlFor={id}>
                        <strong>{r.label}</strong>
                        {r.ownOnly ? (
                          <span className="tag tag-rule" title={t('roles.limited_hint')}>
                            <Icon name="lock" size={12} /> {t('roles.own_only')}
                          </span>
                        ) : null}
                        <span className="muted">{r.hint}</span>
                      </label>
                      {r.permissions?.length ? (
                        <details className="role-perms">
                          <summary>{tn('staff.what_role_can_do', r.permissions.length)}</summary>
                          <PermsByLevel perms={r.permissions} />
                        </details>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </section>
          ))}
        {!shown.length ? <p className="muted">{t('access.no_role_match')}</p> : null}
      </div>
    </div>
  );
}

/** What a role may do, from setting up down to seeing. */
export function PermsByLevel({ perms }: { perms: Perm[] }) {
  const by = (l: Level) => perms.filter((p) => (p.level === 'self' ? 'use' : (p.level ?? 'use')) === l);
  return (
    <div className="perms-by-level">
      {[...LEVELS].reverse().map((l) => {
        const list = by(l);
        if (!list.length) return null;
        return (
          <div key={l}>
            <span className={`level-tag level-${l}`}>{levelLabel(l)}</span>
            <ul>
              {list.map((p) => (
                <li key={p.name}>{p.label}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
